import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';

const toDateInputValue = (date) => {
  const local = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return local.toISOString().slice(0, 10);
};

const getCurrentWeekRange = () => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    startDate: toDateInputValue(monday),
    endDate: toDateInputValue(sunday)
  };
};

const getCurrentWeekRangeValue = () => {
  const range = getCurrentWeekRange();
  return {
    startDate: new Date(`${range.startDate}T00:00:00`).toISOString(),
    endDate: new Date(`${range.endDate}T23:59:59.999`).toISOString(),
    label: 'This Week'
  };
};

const normalizeImages = (images) => {
  if (Array.isArray(images)) return images.filter(Boolean);
  if (!images) return [];
  if (typeof images === 'string') {
    const trimmed = images.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
      } catch (_) {
        return [trimmed];
      }
    }
    return [trimmed];
  }
  return [];
};

const extractCoordinates = (location) => {
  if (!location) return null;
  if (Array.isArray(location?.coordinates) && location.coordinates.length >= 2) {
    return { lat: Number(location.coordinates[1]), lng: Number(location.coordinates[0]) };
  }
  if (location?.coordinates?.lat != null && location?.coordinates?.lng != null) {
    return { lat: Number(location.coordinates.lat), lng: Number(location.coordinates.lng) };
  }
  if (location?.lat != null && location?.lng != null) {
    return { lat: Number(location.lat), lng: Number(location.lng) };
  }
  return null;
};

const getLocationStamp = (location) => {
  if (!location) return 'No location stamp';
  if (location.address && typeof location.address === 'string') return location.address;
  const coords = extractCoordinates(location);
  if (coords && Number.isFinite(coords.lat) && Number.isFinite(coords.lng)) {
    return `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
  }
  return 'No location stamp';
};

const formatDateLabel = (dateStr) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric'
  });

const formatDayLabel = (dateStr) =>
  new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'short'
  });

const formatTimeLabel = (dateValue) =>
  new Date(dateValue).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit'
  });

const formatDateTimeLabel = (dateValue) =>
  new Date(dateValue).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

const getName = (employee) => {
  const first = employee?.firstName || '';
  const last = employee?.lastName || '';
  return `${first} ${last}`.trim() || employee?.email || `Employee ${employee?.id ?? ''}`.trim();
};

const getInitials = (name) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'NA';

const WorklogPage = () => {
  const { company } = useAuth();
  const initialRange = useMemo(() => getCurrentWeekRangeValue(), []);
  const [dateRange, setDateRange] = useState(initialRange);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [workLogs, setWorkLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  const startDate = dateRange?.startDate ? new Date(dateRange.startDate) : null;
  const endDate = dateRange?.endDate ? new Date(dateRange.endDate) : null;

  useEffect(() => {
    if (!company?.id) return;
    const fetchDepartments = async () => {
      try {
        const response = await api.get(`/api/departments/${company.id}`);
        if (response.data?.is_success) {
          setDepartments(response.data.departments || []);
        }
      } catch (err) {
        console.error('Failed to fetch departments:', err);
      }
    };
    fetchDepartments();
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id || !startDate || !endDate) return;
    let cancelled = false;
    const fetchWorkLogs = async () => {
      setLoading(true);
      setError('');
      try {
        const params = {
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString()
        };
        if (selectedDepartment) params.departmentId = selectedDepartment;
        const response = await api.get('/api/worklog/by-role', { params });
        if (!cancelled) {
          if (response.data?.is_success) {
            setWorkLogs(response.data.workLogs || []);
          } else {
            setWorkLogs([]);
            setError('Unable to load worklogs.');
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to fetch worklogs:', err);
          setError('Failed to load worklogs for the selected range.');
          setWorkLogs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchWorkLogs();
    return () => {
      cancelled = true;
    };
  }, [company?.id, startDate?.toISOString(), endDate?.toISOString(), selectedDepartment]);

  const employees = useMemo(() => {
    const map = new Map();
    workLogs.forEach((log) => {
      if (!log?.Employee?.id) return;
      if (!map.has(log.Employee.id)) {
        map.set(log.Employee.id, {
          id: log.Employee.id,
          name: getName(log.Employee),
          profilePicture: log.Employee.profilePicture || ''
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [workLogs]);

  const dateRows = useMemo(() => {
    if (!startDate || !endDate) return [];
    const rows = [];
    const cursor = new Date(startDate);
    cursor.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    while (cursor <= end) {
      rows.push(toDateInputValue(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return rows;
  }, [startDate, endDate]);

  const cellMap = useMemo(() => {
    const map = new Map();
    workLogs.forEach((log) => {
      if (!log?.Employee?.id || !log?.workDate) return;
      const logDate = toDateInputValue(new Date(log.workDate));
      const key = `${logDate}|${log.Employee.id}`;
      const images = normalizeImages(log.images);
      const items = images.map((imageUrl, index) => ({
        id: `${log.id || key}-${index}`,
        imageUrl,
        timeLabel: formatTimeLabel(log.workDate),
        locationLabel: getLocationStamp(log.location),
        description: String(log.description || '').trim(),
        log
      }));
      const existing = map.get(key) || [];
      map.set(key, [...existing, ...items]);
    });
    return map;
  }, [workLogs]);

  const gridTemplateColumns = useMemo(
    () => `var(--row-header-width) repeat(${Math.max(employees.length, 1)}, var(--col-width))`,
    [employees.length]
  );

  return (
    <div className="page-container" style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <div className="page-content">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <h2 style={{ fontSize: 20, margin: 0, color: '#111827', fontWeight: 600 }}>Worklog Timeline</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <HeaderDateFilters
              value={dateRange}
              onChange={(nextRange) => {
                if (nextRange === 'all') {
                  setDateRange(getCurrentWeekRangeValue());
                  return;
                }
                setDateRange(nextRange);
              }}
            />
            <select
              id="worklog-department"
              className="worklog-select"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
              aria-label="Department"
            >
              <option value="">All departments</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {error && <div className="worklog-error-state">{error}</div>}

        {loading ? (
          <div className="worklog-loading">Loading timeline...</div>
        ) : employees.length === 0 ? (
          <div className="worklog-empty-state">No worklog images found for this range.</div>
        ) : (
          <div className="worklog-grid-wrapper">
            <div className="worklog-grid" style={{ gridTemplateColumns }}>
              <div className="worklog-corner-cell">Date / Employee</div>
              {employees.map((employee) => (
                <div key={`header-${employee.id}`} className="worklog-col-header">
                  {employee.profilePicture ? (
                    <img
                      src={employee.profilePicture}
                      alt={employee.name}
                      className="worklog-col-avatar"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  ) : (
                    <span className="worklog-col-initials">{getInitials(employee.name)}</span>
                  )}
                  <span className="worklog-col-name">{employee.name}</span>
                </div>
              ))}

              {dateRows.map((date) => (
                <React.Fragment key={`row-${date}`}>
                  <div className="worklog-row-header">
                    <span className="worklog-row-label">{formatDateLabel(date)}</span>
                    <span className="worklog-row-day">{formatDayLabel(date)}</span>
                  </div>
                  {employees.map((employee) => {
                    const items = cellMap.get(`${date}|${employee.id}`) || [];
                    return (
                      <div key={`cell-${date}-${employee.id}`} className="worklog-cell">
                        {items.length === 0 ? (
                          <div className="worklog-cell-empty">-</div>
                        ) : (
                          <div className="worklog-cell-items">
                            {items.map((item) => (
                              <React.Fragment key={item.id}>
                                <button
                                  type="button"
                                  className="worklog-thumbnail"
                                  onClick={() =>
                                    setSelectedSnapshot({
                                      ...item,
                                      employeeName: employee.name,
                                      description: item.log?.description || '',
                                      status: item.log?.status || 'pending'
                                    })
                                  }
                                  title={`${item.timeLabel} • ${item.locationLabel}`}
                                >
                                  <img src={item.imageUrl} alt="Worklog snapshot" />
                                  <span className="worklog-stamp">
                                    <div className="worklog-stamp-time">{item.timeLabel}</div>
                                    <div className="worklog-stamp-location">{item.locationLabel}</div>
                                  </span>
                                </button>
                                {item.description ? (
                                  <div className="worklog-desc" title={item.description}>
                                    {item.description}
                                  </div>
                                ) : null}
                              </React.Fragment>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {selectedSnapshot && (
          <div className="image-modal" onClick={() => setSelectedSnapshot(null)}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-modal" onClick={() => setSelectedSnapshot(null)}>
                ×
              </button>
              <img src={selectedSnapshot.imageUrl} alt="Worklog snapshot" />
              <div className="worklog-modal-overlay">
                <p className="worklog-modal-overlay-location">{selectedSnapshot.locationLabel}</p>
                <p className="worklog-modal-overlay-time">{formatTimeLabel(selectedSnapshot.log?.workDate)}</p>
                {selectedSnapshot.description ? (
                  <p className="worklog-modal-overlay-desc">{selectedSnapshot.description}</p>
                ) : null}
              </div>
              <div className="worklog-lightbox-meta">
                <h4>{selectedSnapshot.employeeName}</h4>
                <p>{formatDateTimeLabel(selectedSnapshot.log?.workDate)}</p>
                <span className="worklog-status-badge">{selectedSnapshot.status}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorklogPage;
